import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Loader2, LogOut, X } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAD_STATUSES = ["new", "reviewing", "matched", "quoted", "won", "archived"];
const APP_STATUSES = ["new", "reviewing", "approved", "declined"];
const STATUS_STYLES = {
  new: "bg-[#C5A059]/15 text-[#8B5A2B]",
  reviewing: "bg-[#6B7C6E]/15 text-[#4A524C]",
  matched: "bg-[#6B7C6E]/20 text-[#3d4a40]",
  quoted: "bg-[#8B5A2B]/15 text-[#8B5A2B]",
  won: "bg-[#556B2F]/15 text-[#556B2F]",
  archived: "bg-[#1C201D]/8 text-[#758077]",
  approved: "bg-[#556B2F]/15 text-[#556B2F]",
  declined: "bg-[#9c3d33]/10 text-[#9c3d33]",
};

function LoginScreen() {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-[#1C201D] px-5 grain relative">
      <div className="w-full max-w-md border border-[#FAF7F2]/10 bg-[#FAF7F2]/5 p-10 text-center backdrop-blur-md">
        <p className="font-display text-3xl font-medium text-[#FAF7F2]">Estate Outdoors</p>
        <p className="mt-1 text-[0.6rem] font-semibold uppercase tracking-[0.4em] text-[#C5A059]">Team Dashboard</p>
        <p className="mt-6 text-sm leading-relaxed text-[#B9BDB4]">Sign in with your authorized Google account to view quote requests and contractor applications.</p>
        <button onClick={signIn} data-testid="admin-login-google-btn" className="btn-primary mt-8 w-full">
          Sign in with Google
        </button>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [subs, setSubs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, a, g] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/leads`, { withCredentials: true, params: { status: statusFilter, q: search || undefined } }),
        axios.get(`${API}/admin/applications`, { withCredentials: true }),
        axios.get(`${API}/admin/guide-subscribers`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setApps(a.data);
      setSubs(g.data);
    } catch {
      toast.error("Could not load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const updateLead = async (leadId, patch) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, patch, { withCredentials: true });
      setLeads((ls) => ls.map((l) => (l.lead_id === leadId ? { ...l, ...patch } : l)));
      if (selected?.lead_id === leadId) setSelected((s) => ({ ...s, ...patch }));
      toast.success("Lead updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const updateApp = async (appId, status) => {
    try {
      await axios.patch(`${API}/admin/applications/${appId}`, { status }, { withCredentials: true });
      setApps((as) => as.map((a) => (a.application_id === appId ? { ...a, status } : a)));
      toast.success("Application updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/leads-export`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "estate-outdoors-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const openFile = async (path) => {
    try {
      const res = await fetch(`${API}/admin/files/${path}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not open file.");
    }
  };

  const openDetail = (lead) => {
    setSelected(lead);
    setNotes(lead.notes || "");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F2EDE4]">
      <header className="border-b border-[#E0D8CC] bg-[#1C201D]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
          <div>
            <p className="font-display text-2xl text-[#FAF7F2]">Estate Outdoors</p>
            <p className="text-[0.55rem] font-semibold uppercase tracking-[0.4em] text-[#C5A059]">Lead Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-[#B9BDB4]" data-testid="admin-user-email">{user.email}</span>
            <button onClick={onLogout} data-testid="admin-logout-btn" aria-label="Log out" className="flex items-center gap-2 border border-[#FAF7F2]/25 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[#FAF7F2] transition-colors hover:border-[#C5A059] hover:text-[#C5A059]">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {[
            { label: "Total Leads", value: stats?.total_leads },
            { label: "New Leads", value: stats?.by_status?.new ?? 0 },
            { label: "In Progress", value: (stats?.by_status?.reviewing ?? 0) + (stats?.by_status?.matched ?? 0) + (stats?.by_status?.quoted ?? 0) },
            { label: "Contractor Applications", value: stats?.total_applications },
          ].map((s, i) => (
            <div key={s.label} data-testid={`stat-card-${i}`} className="border border-[#E0D8CC] bg-white p-5">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[#758077]">{s.label}</p>
              <p className="mt-2 text-4xl font-semibold tracking-tight">{s.value ?? "—"}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2" role="tablist">
            <button onClick={() => setTab("leads")} data-testid="leads-tab-btn" role="tab" aria-selected={tab === "leads"} className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors ${tab === "leads" ? "bg-[#1C201D] text-[#FAF7F2]" : "border border-[#E0D8CC] bg-white text-[#4A524C] hover:border-[#C5A059]"}`}>
              Quote Leads
            </button>
            <button onClick={() => setTab("applications")} data-testid="applications-tab-btn" role="tab" aria-selected={tab === "applications"} className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors ${tab === "applications" ? "bg-[#1C201D] text-[#FAF7F2]" : "border border-[#E0D8CC] bg-white text-[#4A524C] hover:border-[#C5A059]"}`}>
              Contractor Applications {stats?.new_applications ? <span className="ml-1 text-[#C5A059]">({stats.new_applications} new)</span> : null}
            </button>
            <button onClick={() => setTab("subscribers")} data-testid="subscribers-tab-btn" role="tab" aria-selected={tab === "subscribers"} className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors ${tab === "subscribers" ? "bg-[#1C201D] text-[#FAF7F2]" : "border border-[#E0D8CC] bg-white text-[#4A524C] hover:border-[#C5A059]"}`}>
              Planning Guide {subs.length > 0 ? <span className="ml-1 text-[#C5A059]">({subs.length})</span> : null}
            </button>
          </div>
          {tab === "leads" && (
            <div className="flex flex-wrap gap-3">
              <input
                data-testid="leads-search-input"
                placeholder="Search name, community, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="field-input !w-56 !py-2.5 !text-sm"
              />
              <select data-testid="leads-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="field-input !w-40 !py-2.5 !text-sm" aria-label="Filter by status">
                <option value="all">All statuses</option>
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-2 border border-[#1C201D]/30 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors hover:border-[#C5A059] hover:text-[#8B5A2B]">
                <Download size={14} /> Export CSV
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 size={28} className="animate-spin text-[#C5A059]" /></div>
        ) : tab === "leads" ? (
          <div className="mt-6 overflow-x-auto border border-[#E0D8CC] bg-white" data-testid="leads-table">
            {leads.length === 0 ? (
              <p className="p-10 text-center text-sm text-[#758077]">No leads yet. New quote requests will appear here.</p>
            ) : (
              <table className="w-full min-w-[860px] text-left text-sm">
                <thead>
                  <tr className="border-b border-[#E0D8CC] text-[0.65rem] uppercase tracking-[0.16em] text-[#758077]">
                    <th className="px-5 py-4 font-semibold">Submitted</th>
                    <th className="px-5 py-4 font-semibold">Name</th>
                    <th className="px-5 py-4 font-semibold">Property</th>
                    <th className="px-5 py-4 font-semibold">Investment</th>
                    <th className="px-5 py-4 font-semibold">Status</th>
                    <th className="px-5 py-4 font-semibold">Assigned To</th>
                    <th className="px-5 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.lead_id} className="border-b border-[#E0D8CC]/60 transition-colors hover:bg-[#FAF7F2]">
                      <td className="px-5 py-4 text-[#758077]">{(l.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-4">
                        <p className="font-medium">{l.full_name}</p>
                        <p className="text-xs text-[#758077]">{l.email}</p>
                      </td>
                      <td className="px-5 py-4 text-[#4A524C]">{l.address}</td>
                      <td className="px-5 py-4 text-[#4A524C]">{l.budget_range}</td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`lead-status-${l.lead_id}`}
                          value={l.status}
                          onChange={(e) => updateLead(l.lead_id, { status: e.target.value })}
                          className={`border-0 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider ${STATUS_STYLES[l.status] || ""}`}
                          aria-label={`Status for ${l.full_name}`}
                        >
                          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <input
                          data-testid={`lead-assign-${l.lead_id}`}
                          defaultValue={l.assigned_to}
                          placeholder="Contractor…"
                          onBlur={(e) => e.target.value !== l.assigned_to && updateLead(l.lead_id, { assigned_to: e.target.value })}
                          className="w-36 border border-[#E0D8CC] bg-[#FAF7F2] px-2.5 py-1.5 text-xs outline-none focus:border-[#C5A059]"
                          aria-label={`Assign contractor for ${l.full_name}`}
                        />
                      </td>
                      <td className="px-5 py-4">
                        <button onClick={() => openDetail(l)} data-testid={`lead-view-${l.lead_id}`} className="text-xs font-semibold uppercase tracking-widest text-[#8B5A2B] hover:text-[#C5A059]">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : tab === "applications" ? (
          <div className="mt-6 overflow-x-auto border border-[#E0D8CC] bg-white" data-testid="applications-table">
            {apps.length === 0 ? (
              <p className="p-10 text-center text-sm text-[#758077]">No contractor applications yet.</p>
            ) : (
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-[#E0D8CC] text-[0.65rem] uppercase tracking-[0.16em] text-[#758077]">
                    <th className="px-5 py-4 font-semibold">Submitted</th>
                    <th className="px-5 py-4 font-semibold">Company</th>
                    <th className="px-5 py-4 font-semibold">Trades</th>
                    <th className="px-5 py-4 font-semibold">Experience</th>
                    <th className="px-5 py-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.application_id} className="border-b border-[#E0D8CC]/60 hover:bg-[#FAF7F2]">
                      <td className="px-5 py-4 text-[#758077]">{(a.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-4">
                        <p className="font-medium">{a.company}</p>
                        <p className="text-xs text-[#758077]">{a.full_name} · {a.email} · {a.phone}</p>
                        {a.message && <p className="mt-1 max-w-md truncate text-xs text-[#758077]">{a.message}</p>}
                      </td>
                      <td className="px-5 py-4 text-[#4A524C]">{(a.trades || []).join(", ")}</td>
                      <td className="px-5 py-4 text-[#4A524C]">{a.experience}</td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`app-status-${a.application_id}`}
                          value={a.status}
                          onChange={(e) => updateApp(a.application_id, e.target.value)}
                          className={`border-0 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider ${STATUS_STYLES[a.status] || ""}`}
                          aria-label={`Status for ${a.company}`}
                        >
                          {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto border border-[#E0D8CC] bg-white" data-testid="subscribers-table">
            {subs.length === 0 ? (
              <p className="p-10 text-center text-sm text-[#758077]">No planning-guide downloads yet.</p>
            ) : (
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-[#E0D8CC] text-[0.65rem] uppercase tracking-[0.16em] text-[#758077]">
                    <th className="px-5 py-4 font-semibold">Downloaded</th>
                    <th className="px-5 py-4 font-semibold">Name</th>
                    <th className="px-5 py-4 font-semibold">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.subscriber_id} className="border-b border-[#E0D8CC]/60 hover:bg-[#FAF7F2]">
                      <td className="px-5 py-4 text-[#758077]">{(s.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-4 font-medium">{s.full_name}</td>
                      <td className="px-5 py-4 text-[#4A524C]">{s.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-[#1C201D]/60 backdrop-blur-sm" data-testid="lead-detail-drawer" onClick={() => setSelected(null)}>
          <div className="h-full w-full max-w-lg overflow-y-auto bg-[#FAF7F2] p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="eyebrow !text-[#8B5A2B]">Lead Detail</p>
                <h2 className="mt-2 font-display text-3xl font-light" data-testid="lead-detail-name">{selected.full_name}</h2>
              </div>
              <button onClick={() => setSelected(null)} data-testid="lead-detail-close" aria-label="Close detail" className="p-2 text-[#758077] hover:text-[#1C201D]"><X size={20} /></button>
            </div>
            <dl className="mt-8 space-y-4 text-sm">
              {[
                ["Email", selected.email], ["Phone", selected.phone], ["Property", selected.address],
                ["Type", selected.property_type], ["Size", selected.property_size],
                ["Services", (selected.services || []).join(", ")], ["Start", selected.start_date],
                ["Investment", selected.budget_range], ["Contact via", selected.contact_method],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-4 border-b border-[#E0D8CC]/70 pb-3">
                  <dt className="w-28 shrink-0 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#758077] pt-0.5">{k}</dt>
                  <dd className="text-[#1C201D]">{v || "—"}</dd>
                </div>
              ))}
              <div className="border-b border-[#E0D8CC]/70 pb-3">
                <dt className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#758077]">Vision</dt>
                <dd className="mt-2 leading-relaxed text-[#1C201D]">{selected.description || "—"}</dd>
              </div>
            </dl>
            {(selected.attachments || []).length > 0 && (
              <div className="mt-6">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#758077]">Attachments</p>
                <div className="mt-3 space-y-2">
                  {selected.attachments.map((a) => (
                    <button key={a.path} onClick={() => openFile(a.path)} data-testid="lead-attachment-btn" className="flex w-full items-center gap-2 border border-[#E0D8CC] bg-white px-4 py-2.5 text-left text-sm transition-colors hover:border-[#C5A059]">
                      <FileText size={15} className="text-[#C5A059]" /> {a.filename}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-6">
              <label htmlFor="lead-notes" className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#758077]">Internal Notes</label>
              <textarea id="lead-notes" data-testid="lead-notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="field-input mt-2 resize-y" />
              <button onClick={() => updateLead(selected.lead_id, { notes })} data-testid="lead-notes-save" className="btn-outline-dark mt-3 !px-6 !py-2.5 !text-xs">
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [location.state]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  return (
    <>
      <SEO title="Team Dashboard | Estate Outdoors Calgary" description="Internal dashboard for Estate Outdoors Calgary." />
      {checking ? (
        <div className="flex min-h-screen items-center justify-center bg-[#1C201D]" data-testid="admin-loading">
          <Loader2 size={28} className="animate-spin text-[#C5A059]" />
        </div>
      ) : !user ? (
        <LoginScreen />
      ) : !user.is_admin ? (
        <div data-testid="admin-unauthorized" className="flex min-h-screen items-center justify-center bg-[#1C201D] px-5">
          <div className="max-w-md text-center">
            <p className="font-display text-3xl text-[#FAF7F2]">Access Restricted</p>
            <p className="mt-4 text-sm text-[#B9BDB4]">{user.email} is not an authorized team account. Please sign in with an authorized Google account.</p>
            <button onClick={logout} data-testid="admin-switch-account-btn" className="btn-primary mt-8">Switch Account</button>
          </div>
        </div>
      ) : (
        <Dashboard user={user} onLogout={logout} />
      )}
    </>
  );
}
